import GameManager from '../../cvp-webgames-core/GameManager';
import BaseScenarioPart from '../../cvp-webgames-core/Scenario/BaseScenarioPart';
import { DaarcBaseScenario } from '../DaarcBaseScenario';
import BasePracticeScenario from '../base-practice-scenario/BasePracticeScenario';
import { constants } from '../../misc/constants';
import { DebriefIntro, DebriefVortex } from '../../components/ui/DebriefUI';
import { AssessAirwayActionData, AssessAirwayActions } from '../../components/ui/AssessAirwayUI';
import { VortexUICurrentAttempt } from '../../components/ui/hud/VortexUI';
import { addEmitHelper } from 'typescript';

export default class NathanielClarke extends BasePracticeScenario {
	public cricTextFull: string

	public cricTextLite: string

	public cricText: string

	public get maxScore(): number {
		return 52300;
	}

	public get predeterminedPoints(): number {
		var assessPatientRemoveDenturesPoints = 500;
		var setupFiberOpticsPoints = 1000;
		var awardedPoints = 32700; 
		var cricLitePoints = 1200; 
		
		return assessPatientRemoveDenturesPoints + 
			awardedPoints +
			setupFiberOpticsPoints;		
	}

	public get totalNumberOfQuestions(): number {
		return 5;
	}

	public get currentLevel(): number {
		return 4;
	}

	public getDefaultApneaTime(): number {
		return 210
	}

	public getDefaultSa02(): number {
		return 85
	}

	constructor(gameManager: GameManager, scenarioName: string) {
		super(gameManager, scenarioName);

		this._setupIntro();
		this._setupGeneralNeutralState();
		this._setupProcedures();
	}

	public getDebriefIntro(): DebriefIntro {
		return {
			items: [
				'You have a difficult time conducting an airway assessment due to the patient’s facial edema and the anaphylactic reaction he’s experiencing.',
				'You cannot see the pharynx when you use the laryngoscope, which indicates that the patient can’t be intubated. If you attempt to pass the tube with this view, you may have an esophageal intubation.',
				'The patient’s laryngeal edema prohibits placement of a supraglottic airway.',
				'Did you factor in the clinical condition of the patient in your utilization of the Vortex approach?'
			],
			link: 'https://google.com'
		};
	}

	public getDebriefVortex(): DebriefVortex {
		return {
			successItems: [
				'BVM: ' + this.bvmAttemptCount + ' attempt(s)',
				'ETT: ' + this.ettAttemptCount + ' attempt(s)',
				'SGA: ' + this.sgaAttemptCount + ' attempt(s)',
				'Cricothyrotomy: ' + this.cricAttemptCount + ' attempt(s)'
			],
			askYourselfItems: [
				'Did you exceed the maximum number of optimizations (3) for a given attempt?',
				'Did you optimize between each attempt at the bag valve mask, intubation and supraglottic airway?',
				'Did you follow the Vortex approach by not returning to a technique that you had already attempted with appropriate optimizations and failed?',
				'How can you more efficiently manage the airway? Remember that the quicker you establish a stable, patent airway, the better the patient outcome.'
			]
		};
	}

	public onBVMFinished(): void {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.BVM, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onETTFinished(): void {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onSGAFinished(): void {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.SGA, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public cricTextAssignment(): void {
		this.cricTextFull = 'You successfully performed a cricothyrotomy and established a stable, patent airway for Mr. Clarke ' + this.apneaTimeElapsed + ' after he became apneic. A team arrives shortly thereafter to establish a definitive airway for Mr. Clarke. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points. Review the debrief and then proceed to the summative phase of the game.';
		this.cricTextLite = 'The OORAM provider successfully performed a cricothyrotomy and established a stable, patent airway for Mr. Clarke ' + this.apneaTimeElapsed + ' after he became apneic. A team arrives shortly thereafter to establish a definitive airway for Mr. Clarke. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points. Review the debrief and then proceed to the summative phase of the game.';
		if(this.isLite) {
			this.cricText = this.cricTextLite
		} else this.cricText = this.cricTextFull
	}

	public onCricFinished(): void {
		this.cricAttemptCount = 1;
		this.addScore(this.predeterminedPoints);
		this._CheckOxy();
		this.cricTextAssignment();		
		this.showSuccessPopUpAfterAuscultate(VortexUICurrentAttempt.Cric, this.cricText);
	}

	public _CheckOxy() {
		if(!this.preOxDone) {
			this.debriefLogFailedItem("Pre-Oxygenation: You did not pre-oxygenate the patient.")
		};
		if(!this.callFlexBr) {
			this.debriefLogFailedItem("Call for flexible bronchoscope: Recognizing the need for a flexible bronchoscope early is critical, especially when the patient’s condition indicates intubation and use of a supraglottic airway will be anatomically impossible.")
		}	
	}

	public getAssessAirwayData(): AssessAirwayActionData[] {
		const context = this;
		return [
			{
				relatedAction: AssessAirwayActions.ExamineNeck,
				title: 'Examine Neck',
				text: 'His larynx is free and mobile. You can palpate the thyroid and cricoid cartilage, as well as the cricothyroid membrane.'
			},
			{
				relatedAction: AssessAirwayActions.LiftChinToCeiling,
				title: 'Lift chin to ceiling',
				text: 'Thyromental Distance: 4 finger breadths (4 cm) <br /> Hyoid to Thyroid Notch Distance: 2 finger breadths (1 cm)'
			},
			{
				relatedAction: AssessAirwayActions.PutChinToChest,
				title: 'Put chin to chest',
				text: 'No feedback to display'
			},
			{
				relatedAction: AssessAirwayActions.RemoveDentures,
				title: 'Remove Dentures',
				text: 'Dentures have been removed',
				noPoints: true
			},
			{
				relatedAction: AssessAirwayActions.ShowTeeth,
				title: 'Smile for me',
				text: 'The patient indicated that he has dentures.'
			},
			{
				relatedAction: AssessAirwayActions.StickOutTongue,
				title: 'Open mouth',
				text: 'Inter-incisor Distance: 3 finger breadths (4 cm)',
				imgUrl:'./assets/images/assess-airway-images/FacialEdma.png'
			}
		]
	}

	private _setupIntro() {
		// this.intro.config.part1Config = {
		// 	videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
		// 	dialogs: [
		// 		{
		// 			speakerName: constants.speakers.RnMiranda,
		// 			text: `
		// 			Patient name is Nathaniel Clarke. He’s a 70-year-old male, 70 in, 100 kg. 
		// 			He has hypertension, hyperlipidemia, and coronary artery disease. 
		// 			He came in with a small bowel obstruction and just returned from radiology. 
		// 			He’s experiencing a delayed anaphylactic reaction to contrast with itching, 
		// 			a rash, and is wheezing. His K is 4.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario4 - intro - 53'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.DrCaleb,
		// 			text: `
		// 				I’m already administering epinephrine and steroids. I’ll manage the
		// 				hemodynamics, you manage the airway.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario4 - intro - 53'
		// 		}
		// 	],
		// 	sbarConfig: {
		// 		s: `Nathaniel Clarke came in with a small bowel obstruction and just returned from radiology. He’s experiencing a delayed anaphylactic reaction to contrast with itching, a rash, and is wheezing. His K is 4.`,
		// 		b: `
		// 			Age: 70<br />
		// 			Height (in): 70 in<br />
		// 			Weight (kg): 100 kg<br />
		// 			Potassium (K): 4.0<br />
		// 			NPO Status: He ate 2 hours ago<br />
		// 			<br />
		// 			PMH: Nathaniel Clarke has hypertension, hyperlipidemia, and coronary artery disease.
		// 		`,
		// 		a: `He is already receiving epinephrine and steroids. The physician in the room is managing the hemodynamics and needs you to manage the airway.`,
		// 		r: `While the hemodynamics are being managed, we need a definitive airway.`
		// 	}
		// }

		this._id_ScenarioSet(4); // <<<<<< CRITICAL: must be set in every scenario. This number does not always match the "level number" variable set in the declarations above, use the documented level numbers and not how they are listed in game <<<<<<<<

		this.intro.config.part1Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			chapters: [
				{
					chapterName: () => 'DAARC - Scenario4 - intro - 53'
				}
			],
			sbarConfig: {
				s: `Nathaniel Clarke came in with a small bowel obstruction and just returned from radiology. He's experiencing a delayed anaphylactic reaction to contrast with itching, a rash, and is wheezing. His K is 4.`,
				b: `
					Age: 70<br />
					Height (in): 70 in<br />
					Weight (kg): 100 kg<br />
					Potassium (K): 4.0<br />
					NPO Status: He ate 2 hours ago<br />
					<br />
					PMH: Nathaniel Clarke has hypertension, hyperlipidemia, and coronary artery disease.
				`,
				b508: `
					Age. 70.
					Height. 70 inches.
					Weight. 100 kilograms.
					Potassium. 4.0.
					NPO Status. He ate 2 hours ago.
					
				 	Past Medical History. Nathaniel Clarke has hypertension hyperlipidemia and coronary artery disease.
				`,
				a: `He is already receiving epinephrine and steroids. The physician in the room is managing the hemodynamics and needs you to manage the airway.`,
				r: `While the hemodynamics are being managed, we need a definitive airway.`
			}
		}
	}

	private _setupGeneralNeutralState() {		
		this.neutralState.config.part1Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			hudIdleVideoChapter: () =>'DAARC - Scenario4 - Flat-rebreather-loop - 54',
			onCalledForVortex: () => {
				this.debriefLog('Called for a Vortex reader')
			},
			isCric: true,
		};

		var toolsArr = null
		if(this.isLite) {
			toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,	
				constants.toolbagTools.cric //comment this out on cric not required scenarios		
			]
		} else {			
				toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,
				constants.toolbagTools.cric
			]
		}

		this.neutralState.config.part2Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			toolsToChooseFrom: toolsArr,
			onTeamNotDebriefed: () => {
				this.debriefLogFailedItem("Brief other staff in the room about attempt order: Briefing your staff on the attempt order will help them anticipate providing the right equipment. Briefings empower your staff to notify you if you fall into fixation error.")
            },
            onTeamDebriefed: () => {
                this.debriefLog('Briefed other staff in the room about attempt order');
            },
            onToolsOrganized: () => {
                this.debriefLog('Organized equipment');				
            }
		};

		this.neutralState.config.part3Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			waitForSuctionVideoChapter: () =>'DAARC - Scenario4 - Flat-rebreather-loop - 54',
			onSuctionSet: () => {
				this.debriefLog("Suction Set") 
			}
		};

		this.neutralState.config.part4Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			waitForAdjustBedHeightVideoChapter: () =>'DAARC - Scenario4 - 45 - Rebreather - 11',
			onAdjustedBedHeight: () => {
				this._id_bedPos = this._id_bedAdj; // <<<<<<<
				this.debriefLog("Bed Adjusted");
			}
		};

		this.neutralState.config.part5Config = { 
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			liftChinVideoChapter: () =>'DAARC - Scenario4 - 45 - patient-assessment-up - 36',
			chinToChestVideoChapter: () =>'DAARC - Scenario4 - 45 - patient-assessment-down - 37',
			examineNeckVideoChapter: () =>'DAARC - Scenario4 - 45 - patient-assessment-straight - 35',
			realTeethVideoChapter: () =>'DAARC - Scenario4 - 45 - patient-assessment-straight - 35',
			stickOutTongueVideoChapter: () =>'DAARC - Scenario4 - 45 - patient-assessment-straight - 35',
			onAssessmentFinied: () => {
				this.debriefLog("Performed a complete airway assessment")
				//potentially may need a return to 'DAARC - Scenario4 - 45 - Nothing - 7' after completing
			}
		};

		this.neutralState.config.part6Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			waitForFiberOpticVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onSetupFiberOptic: () => {
				this.callFlexBr = true;
				this.debriefLog("Called for advanced airway cart")
			}
		};

		this.neutralState.config.part7Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			waitForPreOxygenateVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // this needs to update based on the selection made		
			//waitForPreOxygenateVideoChapter: () =>'DAARC - Scenario4 - 45 - Nothing - 7',
			onPreOxygenated: (selection: number) => {
				this.preOxDone = true;
				this._id_UpdatePreOxText(selection); // this should update the pre-oxygenation neutral video segment, but the reference set above might not reflect this update
				switch(selection) {
					case 0:
						this.debriefLog("Pre-Oxygenated with High Flow Nasal Cannula");
						break;
					case 1:
						this.debriefLog("Pre-Oxygenated with Nasal Noninvasive Positive Pressure");
						break;
					case 2:
						this.debriefLog("Pre-Oxygenated with 100% Non-Rebreather Mask");
						break;
					case 3:
						this.debriefLogFailedItem("Pre-Oxygenated with BVM. This may not be the best solution");
						break;
				}
			}
		};

		this.neutralState.config.part8Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			meds: [
				{
					title: 'Induction Agent',
					audioPrefix: 'InductionAgent_'
				},
				{
					title: 'Administer Neuromuscular Junction Blocker',
					audioPrefix: 'NMJB_'
				}
			],
			onApneaStartedVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // APNEA TIMER INPUT SEGMENT CHANGE HERE,
			onPatientFullyMedicated: () => {	
				this.debriefSetAdministeringText("You correctly medicated your patient and earned maximum points.")
			},
			onSingleMedAdministered: () => {
				this.debriefSetAdministeringText("Did you administer all medications that are appropriate, given the patient's condition?") //May need more description			
			}
		};
	}

	private _setupProcedures() {
		this._setupBvm();
		this._setupEtt();
		this._setupSga();
		this._setupCric();
		this._setupAuscultate();
	}

	private _setupBvm() {		
		this.procedures.bvm.config.part1Config = {			
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			// missing segment callback here
			chooseHandVideoChapter: () => this._id_bvmHandAttempt,
			onChooseHandNextClicked: (isOneHanded: boolean) => {
				if(isOneHanded) {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(1)) // set segment variable equal to this
				} else {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(2)) // set segment variable equal to this
				}
			}
		};
		this.procedures.bvm.config.part2Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			onProcedureAttempted: (attemptCount: number) => {
				this.bvmAttemptCount = attemptCount;
			}
		}
	}

	private _setupEtt() {
		this.procedures.ett.config.part1Config = {
			hideVlButton: () => true,			
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),			
			insertingETTVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),			
			//squeezeAmbuBagVideoChapter: () =>'DAARC - Scenario4 - 45 - ETT - No PreOx - 13',
			ettSpecialUIConfig: {
				airwayImage: () => {
					return './assets/images/ett/Grade4Edema.png';
				},
				magillForceps: {
					text: () => 'There is no foreign body in the patient’s airway.'
				},
				suction: {
					text: () => ''
				},
				manipulateLarynx: {
					text: () => ''
				}
			},
			onProcedureAttempted: (attemptCount: number) => {
				this.ettAttemptCount = attemptCount;
			}
			
		};
	}

	private _setupSga() {
		this.procedures.sga.config.part1Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			squeezeAmbuBagVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			insertingSgaVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			//squeezeAmbuBagVideoChapter: () =>'DAARC - Scenario4 - 45 - SGA - No PreOx - 16',
			onProcedureAttempted: (attemptCount: number) => {
				this.sgaAttemptCount = attemptCount;
			}
		};
	}

	private _setupCric() {
		this.procedures.cric.config.isAttemptSuccessful = () => {
			return true;
		}		

		this.procedures.cric.config.isCricScenario = true;

		this.procedures.cric.config.part1Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			correctToolOrder: [
				'Scalpel',
				'Spreader',
				'Bougie',
				'ETT',
				'Syringe',
			]
		};
		this.procedures.cric.config.part2Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			identifyLandmarksVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(1), 'flat'),
			//identifyLandmarksVideoChapter: () =>'DAARC - Scenario4 - intro - 53',
			onLandmarksFinished: (isCorrect: boolean) => {
				if(isCorrect) {
					this.debriefLog("Selected the correct landmarks for cric");
				} else {
					this.debriefLogFailedItem("failed to select the correct landmarks for cric");
				}
			}
		};
		this.procedures.cric.config.part3Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4'
		};
		this.procedures.cric.config.part4Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			scalpelVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(2), 'flat')
			//scalpelVideoChapter: () =>'DAARC - Scenario4 - intro - 53'
		};
		this.procedures.cric.config.part5Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			spreaderVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(3), 'flat')
			//spreaderVideoChapter: () =>'DAARC - Scenario4 - intro - 53'
		};
		this.procedures.cric.config.part6Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			bougieVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(4), 'flat')
			//bougieVideoChapter: () =>'DAARC - Scenario4 - intro - 53'
		};
		this.procedures.cric.config.part7Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			etTubeVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(5), 'flat')
			//etTubeVideoChapter: () =>'DAARC - Scenario4 - intro - 53'
		};
		this.procedures.cric.config.part8Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(6), 'Flat')
			//squeezeAmbuBagVideoChapter: () =>'DAARC - Scenario4 - intro - 53'
		};

		this.procedures.cric.config.part9Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			onYesClicked: () => {
				this.addScore(1200) // lite cric points that are normally awarded during cric
			},
			onNoClicked: () => {
				this.showFailedScenarioPopUp('You absolutely need to have the level 3 OORAM provider perform the cric.','Failed Scenario')
			}
		};
	}

	private _setupAuscultate() {
		this.procedures.auscultate.config.part1Config = {
			videoUrl: './assets/videos/scenario4/daarc-scenario4-nathanielclarke-v1.mp4',
			bvmConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_bvmAusc),
				//moveToAuscultateScreenVideoChapter: () =>'DAARC - Scenario4 - intro - 53',
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo
				}
			},
			ettConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_ettAusc),
				//moveToAuscultateScreenVideoChapter: () =>'DAARC - Scenario4 - intro - 53',
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo
				}
			},
			sgaConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_sgaAusc),
				//moveToAuscultateScreenVideoChapter: () =>'DAARC - Scenario4 - intro - 53',
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo
				}
			},
			cricConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_cricAusc, 'flat'),
				//moveToAuscultateScreenVideoChapter: () =>'DAARC - Scenario4 - intro - 53',
				checkRiseAndTubeHumidificationText: () => this.auscSuccess,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathYes
				}
			}
		};
	}


	public getParts(): BaseScenarioPart<DaarcBaseScenario>[] {
		return this.parts;
	}

	public getSoundPath(fileName: string): string {
		return './assets/audio/scenario1/' + fileName + '.ogg';
	}

	public showDefaultSideMenu() {
		const context = this;
		context.sideMenuId = context.uiWrapper.displaySideMenu({
			gameManager: this.gameManager,
			inductionTimeInSeconds: 30,
			apneaTimeInSeconds: 210,
			bloodPressureNumerator: 95,
			bloodPressureDenominator: 60,
			bloodPressureMinNumerator: 50,
			bloodPressureMinDenominator: 33,
			bloodPressureMaxNumerator: 220,
			bloodPressureMaxDenominator: 110,
			sa02: 85,
			heartRate: 125,
			heartRateMax: 200,
			respirationRate: 32,
			resetEtc02: false,
			etc02: 35,
			showETC02: false,
			isApneaTimerIdle: true,
			onInductionTimerRanOut: () => {
				context.hasInductionTimerRanOut = true;
			},
			onApneaTimerRanOut: () => {
                context.failedScenario = true;
				this._CheckOxy();
				context.onShowSuccessFailedAttempt(constants.attemptText.failedTitle, 'You did not establish a stable, patent airway in time for Nathaniel Clarke. He has expired. Review the debrief and then try again.', true);
			}
		}, context.sideMenuId);
	}
}